import { Injectable, inject } from "@angular/core";
import { HttpClient, httpResource, type HttpResourceRef } from "@angular/common/http";
import { firstValueFrom } from "rxjs";

import { environment } from "../../../environments/environment";

import type { IBrandingUpdate } from "../models/i-branding-update";
import type { IOrganizationFeatures } from "../models/i-organization-features";
import type { ICreateMemberResult } from "../models/i-create-member-result";
import type { IOrganization, PortalConnection } from "../models/i-organization";
import type { IOrganizationDelta } from "../models/i-organization-delta";
import type { IOrganizationPruneReport } from "../models/i-organization-prune-report";
import type { IOrganizationSyncReport } from "../models/i-organization-sync-report";
import type { IOrganizationWithMembers } from "../models/i-organization-with-members";
import type { ISlugAvailability } from "../models/i-slug-availability";

export interface ICreateOrganizationRequest {

  slug: string;

  displayName: string;


  connections: PortalConnection[];

  branding: {
    logoUrl: string;
    primary: string;
    primaryHover: string;
    primarySoft: string;
    onPrimary: string;
    ink: string;
  };

  /**
   * The customer this organization will show data for, from the wizard's
   * picker. Null when nobody onboarding knew it yet — that is a legitimate
   * state, not a blocked onboarding.
   */
  customerId: string | null;

  tenantId: string | null;

  /** The optional pages the organization starts with, both on unless switched off. */
  features?: IOrganizationFeatures;
}

export interface ICreateOrganizationResult {

  organization: IOrganization;

  /**
   * The system account onboarding created (`system-account@<slug>.priv`),
   * with its temporary password. Shown once on the result screen; it can be
   * revealed again from the organization's Users tab.
   */
  systemAccount: ICreateMemberResult;
}

/**
 * The Davaco-Admin surface. Every call here 403s for a customer token — the API
 * re-checks the role on the verified token, so nothing on this client is an
 * authorization decision.
 */
@Injectable({ providedIn: "root" })
export class OrganizationsService {

  private readonly http = inject(HttpClient);

  public readonly organizations: HttpResourceRef<IOrganization[]> =
    httpResource<IOrganization[]>(() => `${environment.apiBaseUrl}/portal/organizations`, {
      defaultValue: [],
    });

  public async checkSlug(slug: string): Promise<ISlugAvailability> {
    return firstValueFrom(
      this.http.get<ISlugAvailability>(
        `${environment.apiBaseUrl}/portal/organizations/slug-available`,
        { params: { slug } }
      )
    );
  }

  public async create(request: ICreateOrganizationRequest): Promise<ICreateOrganizationResult> {
    const result: ICreateOrganizationResult = await firstValueFrom(
      this.http.post<ICreateOrganizationResult>(
        `${environment.apiBaseUrl}/portal/organizations`,
        request
      )
    );

    // The list is a resource, so it will not notice a POST on its own.
    this.organizations.reload();
    return result;
  }

  public async get(slug: string): Promise<IOrganization> {
    return firstValueFrom(
      this.http.get<IOrganization>(
        `${environment.apiBaseUrl}/portal/organizations/${encodeURIComponent(slug)}`
      )
    );
  }

  public async update(
    slug: string,
    patch: Partial<
      Pick<
        IOrganization,
        "displayName" | "tier" | "connections" | "customerId" | "tenantId"
      >
    > & { branding?: Partial<IBrandingUpdate>; features?: Partial<IOrganizationFeatures> }
  ): Promise<IOrganization> {
    const updated: IOrganization = await firstValueFrom(
      this.http.patch<IOrganization>(
        `${environment.apiBaseUrl}/portal/organizations/${encodeURIComponent(slug)}`,
        patch
      )
    );

    this.organizations.reload();
    return updated;
  }

  /**
   * Start creating (or retry) the customer's `<slug>.<base host>` address.
   *
   * Answers at once with hostnameStatus `securing`; the API finishes in the
   * background — DNS has to propagate before Azure will bind — and records
   * `secured` or `failed` on the organization.
   */
  public async provisionHostname(slug: string): Promise<IOrganization> {
    const organization: IOrganization = await firstValueFrom(
      this.http.post<IOrganization>(
        `${environment.apiBaseUrl}/portal/organizations/${encodeURIComponent(slug)}/hostname`,
        {}
      )
    );

    this.organizations.reload();
    return organization;
  }

  /**
   * Copy the portal's Auth0 organizations into the organization store.
   *
   * `apply: false` is a dry run: the API writes nothing and reports what would
   * change, which is how the dashboard previews a sync before committing it.
   */
  public async sync(apply: boolean): Promise<IOrganizationSyncReport> {
    const report: IOrganizationSyncReport = await firstValueFrom(
      this.http.post<IOrganizationSyncReport>(
        `${environment.apiBaseUrl}/portal/organizations/sync`,
        null,
        { params: { apply: String(apply) } }
      )
    );

    if (apply) {
      this.organizations.reload();
    }
    return report;
  }

  /** The organization store against Auth0, field by field. Read-only. */
  public async delta(): Promise<IOrganizationDelta> {
    return firstValueFrom(
      this.http.get<IOrganizationDelta>(`${environment.apiBaseUrl}/portal/organizations/delta`)
    );
  }

  /**
   * Remove stored organizations with no Auth0 organization behind them.
   * `apply: false` is a dry run that only names them.
   */
  public async prune(apply: boolean): Promise<IOrganizationPruneReport> {
    const report: IOrganizationPruneReport = await firstValueFrom(
      this.http.post<IOrganizationPruneReport>(
        `${environment.apiBaseUrl}/portal/organizations/prune`,
        null,
        { params: { apply: String(apply) } }
      )
    );

    if (apply) {
      this.organizations.reload();
    }
    return report;
  }

  /** Every portal organization with its Auth0 users. */
  public async members(): Promise<IOrganizationWithMembers[]> {
    return firstValueFrom(
      this.http.get<IOrganizationWithMembers[]>(
        `${environment.apiBaseUrl}/portal/organizations/members`
      )
    );
  }
}
